/**
 * Utility functions for fitting text within a given width by calculating the optimal font size.
 * Uses canvas measureText() for accurate text width measurement.
 */

// Create a single canvas for measurements (reused for performance)
let measureCanvas: HTMLCanvasElement | null = null;
let measureCtx: CanvasRenderingContext2D | null = null;

export function getMeasureContext(): CanvasRenderingContext2D | null {
  if (!measureCanvas) {
    measureCanvas = document.createElement('canvas');
    measureCtx = measureCanvas.getContext('2d');
  }
  return measureCtx;
}

/**
 * Calculate the optimal font size that makes text fit within a given width.
 * Uses binary search for efficiency.
 * 
 * @param text - The text to measure
 * @param fontFamily - The font family to use
 * @param maxWidth - Maximum width in pixels
 * @param maxFontSize - Starting/maximum font size in pixels (default: 24)
 * @param minFontSize - Minimum font size in pixels (default: 6)
 * @returns The calculated font size in pixels
 */
export function calculateFitFontSize(
  text: string,
  fontFamily: string,
  maxWidth: number,
  maxFontSize: number = 24,
  minFontSize: number = 6
): number {
  const ctx = getMeasureContext();
  if (!ctx || !text || maxWidth <= 0) return maxFontSize;

  // Binary search for optimal font size
  let low = minFontSize;
  let high = maxFontSize;
  let bestFit = minFontSize;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    ctx.font = `${mid}px ${fontFamily}`;
    const textWidth = ctx.measureText(text).width;

    if (textWidth <= maxWidth) {
      bestFit = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return bestFit;
}

/**
 * Calculate the optimal font size for canvas export.
 * Similar to calculateFitFontSize but uses the provided canvas context.
 * 
 * @param ctx - Canvas 2D rendering context
 * @param text - The text to measure
 * @param fontFamily - The font family to use
 * @param maxWidth - Maximum width in pixels
 * @param maxFontSize - Starting/maximum font size in pixels
 * @param minFontSize - Minimum font size in pixels (default: 6)
 * @returns The calculated font size in pixels
 */
export function calculateFitFontSizeCanvas(
  ctx: CanvasRenderingContext2D,
  text: string,
  fontFamily: string,
  maxWidth: number,
  maxFontSize: number,
  minFontSize: number = 6
): number {
  if (!text || maxWidth <= 0) return maxFontSize;

  // Binary search for optimal font size
  let low = minFontSize;
  let high = maxFontSize;
  let bestFit = minFontSize;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    ctx.font = `${mid}px ${fontFamily}`;
    const textWidth = ctx.measureText(text).width;

    if (textWidth <= maxWidth * 0.95) { // 95% to leave small margin
      bestFit = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return bestFit;
}

/**
 * Calculate the maximum number of characters that can fit in a given width.
 * Uses average character width from a representative sample for realistic estimation.
 * 
 * @param fontFamily - The font family to use
 * @param fontSizePx - Font size in pixels
 * @param fieldWidthPercent - Field width as percentage (0-100)
 * @param referenceContainerWidth - Reference container width in pixels (default: 500)
 * @returns Maximum number of characters that fit
 */
export function calculateMaxCharsForField(
  fontFamily: string,
  fontSizePx: number,
  fieldWidthPercent: number,
  referenceContainerWidth: number = 500
): number {
  const ctx = getMeasureContext();
  if (!ctx || fieldWidthPercent <= 0 || fontSizePx <= 0) return 50;

  // Calculate field width in pixels
  const fieldWidthPx = referenceContainerWidth * (fieldWidthPercent / 100);
  
  // Set font for measurement
  ctx.font = `${fontSizePx}px ${fontFamily}`;
  
  // Use a representative sample of uppercase letters and numbers for average width
  // This gives a more realistic estimate than using only the widest character
  const sampleText = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const sampleWidth = ctx.measureText(sampleText).width;
  const avgCharWidth = sampleWidth / sampleText.length;
  
  if (avgCharWidth <= 0) return 50;
  
  // Calculate max chars (use 98% of width to leave tiny margin)
  const maxChars = Math.floor((fieldWidthPx * 0.98) / avgCharWidth);
  
  // Return at least 1, at most 200
  return Math.max(1, Math.min(200, maxChars));
}
